import { API_CONFIG } from '@/constants/api';
import { getAuthToken, removeAuthToken } from '@/utils/auth';

function mapPdfErrorMessage(status: number, rawMessage?: string): string {
  if (status === 401) {
    return 'Sessão expirada ou acesso não autorizado. Faça login novamente.';
  }
  if (status === 403) {
    return 'Você não tem permissão para exportar este prontuário.';
  }
  if (status === 404) {
    return 'Prontuário não encontrado.';
  }
  if (rawMessage && /invalid time value/i.test(rawMessage)) {
    return 'Não foi possível gerar o PDF devido a dados de data inválidos no prontuário.';
  }
  if (rawMessage?.trim()) {
    return rawMessage.trim();
  }
  if (status >= 500) {
    return 'Erro ao gerar PDF. Tente novamente mais tarde.';
  }
  return 'Falha ao gerar PDF';
}

function handleUnauthorizedSession(): void {
  if (typeof window === 'undefined') return;
  const currentPath = window.location.pathname;
  if (currentPath.includes('/login')) return;
  removeAuthToken();
  window.location.href = `/login?error=session_expired&redirect=${encodeURIComponent(currentPath)}`;
}

async function downloadAuthenticatedPdf(endpoint: string, fileName: string): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    handleUnauthorizedSession();
    throw new Error('Sessão expirada ou acesso não autorizado. Faça login novamente.');
  }

  const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });

  if (!response.ok) {
    let rawMessage = '';
    try {
      const body = (await response.json()) as { message?: string; error?: string };
      rawMessage = body.error || body.message || '';
    } catch {
      rawMessage = response.statusText || '';
    }

    if (response.status === 401) {
      handleUnauthorizedSession();
    }

    throw new Error(mapPdfErrorMessage(response.status, rawMessage));
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function downloadPatientRecordPdf(patientId: string): Promise<void> {
  if (!patientId?.trim()) {
    return Promise.reject(new Error('Identificação do paciente não encontrada.'));
  }

  return downloadAuthenticatedPdf(
    `/medical-records/patient/${patientId}/pdf`,
    `prontuario-paciente-${patientId}.pdf`,
  );
}

export function downloadDoctorProfilePdf(doctorId: string): Promise<void> {
  if (!doctorId?.trim()) {
    return Promise.reject(new Error('Identificação do médico não encontrada.'));
  }

  return downloadAuthenticatedPdf(
    `/doctors/${doctorId}/profile-pdf`,
    `prontuario-medico-${doctorId}.pdf`,
  );
}
